import { useEffect, useMemo, useState } from "react";
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { withTranslation } from "react-i18next";
import { BiNetworkChart, BiPlus, BiTrash } from "react-icons/bi";
import { SANKEY_NODE_TYPE } from "../../../core/constants/common";
import StandardService from "../../../core/service/StandardService";
import "./SankeyDiagramConfigModal.scss";

const emptyNode = () => ({
	type: SANKEY_NODE_TYPE.METRIC,
	refId: null,
	children: []
});

const refIdFromRaw = (raw) => {
	if (!raw || typeof raw !== "object") return null;
	const id =
		raw.refId ??
		raw.ref_id ??
		raw.metricId ??
		raw.datapointId ??
		raw.dataPointId;
	return id === undefined || id === null ? null : id;
};

const optionsMatchRef = (optionId, refId) => {
	if (optionId === undefined || optionId === null) return false;
	if (refId === undefined || refId === null) return false;
	return String(optionId) === String(refId);
};

function treeHasUnresolvableRefs(nodes, metrics, dataPoints) {
	const check = (n) => {
		if (!n) return false;
		if (n.refId != null) {
			const list = n.type === SANKEY_NODE_TYPE.DP ? dataPoints : metrics;
			if (!list.some((o) => optionsMatchRef(o.id, n.refId))) return true;
		}
		return (n.children || []).some(check);
	};
	return (nodes || []).some(check);
}

/** Build in-memory tree from `value` — only `type`, `refId`, `children` (METRIC | DP). */
function parseSankeyNode(raw) {
	if (!raw || typeof raw !== "object") return null;
	const type = raw.type === SANKEY_NODE_TYPE.DP ? SANKEY_NODE_TYPE.DP : SANKEY_NODE_TYPE.METRIC;
	const children = Array.isArray(raw.children)
		? raw.children.map(parseSankeyNode).filter(Boolean)
		: [];
	return {
		type,
		refId: refIdFromRaw(raw),
		children
	};
}

function parseSankeyTree(arr) {
	if (!Array.isArray(arr)) return [];
	return arr.map(parseSankeyNode).filter(Boolean);
}

function persistedSankeyNode(n) {
	return {
		type: n.type,
		refId: n.refId ?? null,
		children: Array.isArray(n.children) ? n.children.map(persistedSankeyNode) : []
	};
}

function persistedSankeyTree(arr) {
	return arr.map(persistedSankeyNode);
}

const SankeyNodeEditor = ({ t, node, metrics, dataPoints, onChange, onDelete, branchKey }) => {
	const metricOptions = useMemo(() => metrics ?? [], [metrics]);
	const dataPointOptions = useMemo(() => dataPoints ?? [], [dataPoints]);

	const handleTypeChange = (e) => {
		const newType = e.target.value;
		onChange({ ...node, type: newType, refId: null });
	};

	const addChild = () => {
		const updated = { ...node, children: [...(node.children || []), emptyNode()] };
		onChange(updated);
	};

	const updateChild = (idx, updatedChild) => {
		const next = { ...node, children: [...node.children] };
		next.children[idx] = updatedChild;
		onChange(next);
	};

	const deleteChild = (idx) => {
		const next = { ...node, children: node.children.filter((_, i) => i !== idx) };
		onChange(next);
	};

	const isMetric = node.type === SANKEY_NODE_TYPE.METRIC;
	const refOptions = isMetric ? metricOptions : dataPointOptions;
	const selectedRef = useMemo(() => {
		if (node.refId == null) return [];
		const found = refOptions.find((o) => optionsMatchRef(o.id, node.refId));
		return found ? [found] : [];
	}, [refOptions, node.refId]);

	const refLabelKey = (option) =>
		`${option.code} - ${option.name ?? option.title ?? ""}`;

	const selectedRefLabel = selectedRef[0] ? refLabelKey(selectedRef[0]) : undefined;

	return (
		<li>
			<div className="sankey-config-node__row">
				<div className="sankey-config-node__field sankey-config-node__field--type">
					<label className="mb-1 small text-muted">{t("SankeyDiagramConfigModal.node_type.label")}</label>
					<select className="form-select form-select-sm" value={node.type} onChange={handleTypeChange}>
						<option value={SANKEY_NODE_TYPE.METRIC}>{t("SankeyDiagramConfigModal.metric.label")}</option>
						<option value={SANKEY_NODE_TYPE.DP}>{t("SankeyDiagramConfigModal.datapoint.label")}</option>
					</select>
				</div>
				<div className="sankey-config-node__field sankey-config-node__field--ref">
					<label className="mb-1 small text-muted" htmlFor={`sankey-ref-${branchKey}`}>
						{isMetric ? t("SankeyDiagramConfigModal.select_metric.label") : t("SankeyDiagramConfigModal.select_datapoint.label")}
					</label>
					<Typeahead
						id={`sankey-ref-${branchKey}`}
						key={`sankey-ref-${branchKey}-${isMetric ? "m" : "dp"}`}
						className="sankey-config-ref-typeahead"
						clearButton
						positionFixed
						flip
						align="justify"
						maxHeight="280px"
						selected={selectedRef}
						options={refOptions}
						labelKey={refLabelKey}
						onChange={(sel) => {
							const item = sel[0];
							onChange({ ...node, refId: item ? item.id : null });
						}}
						placeholder={t("Search.label")}
						aria-label={
							isMetric
								? t("SankeyDiagramConfigModal.select_metric.label")
								: t("SankeyDiagramConfigModal.select_datapoint.label")
						}
						inputProps={{ className: "form-control form-control-sm", title: selectedRefLabel }}
						renderMenuItemChildren={(option) => (
							<div className="sankey-config-ref-option" title={refLabelKey(option)}>
								{refLabelKey(option)}
							</div>
						)}
					/>
				</div>
				<div className="sankey-config-node__actions">
					<OverlayTrigger
						placement="top"
						overlay={<Tooltip id={`sankey-add-${branchKey}`}>{t("SankeyDiagramConfigModal.add_child.label")}</Tooltip>}
					>
						<Button
							variant="GroupStake"
							className="btn-outline-primary"
							onClick={addChild}
							type="button"
							aria-label={t("SankeyDiagramConfigModal.add_child.label")}
						>
							<BiPlus aria-hidden />
						</Button>
					</OverlayTrigger>
					<OverlayTrigger
						placement="top"
						overlay={<Tooltip id={`sankey-del-${branchKey}`}>{t("Delete.label")}</Tooltip>}
					>
						<Button
							variant="GroupStake"
							className="btn-outline-danger"
							onClick={onDelete}
							type="button"
							aria-label={t("Delete.label")}
						>
							<BiTrash aria-hidden />
						</Button>
					</OverlayTrigger>
				</div>
			</div>
			{node.children && node.children.length > 0 && (
				<ul className="sankey-config-tree">
					{node.children.map((child, idx) => (
						<SankeyNodeEditor
							key={`${branchKey}-${idx}`}
							t={t}
							branchKey={`${branchKey}-${idx}`}
							node={child}
							metrics={metrics}
							dataPoints={dataPoints}
							onChange={(updatedChild) => updateChild(idx, updatedChild)}
							onDelete={() => deleteChild(idx)}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

const SankeyDiagramConfigModalBase = ({ t, open, onClose, onSave, value, standardId, topicId = 0 }) => {
	const [tree, setTree] = useState(() => parseSankeyTree(Array.isArray(value) ? value : []));
	const [metrics, setMetrics] = useState([]);
	const [dataPoints, setDataPoints] = useState([]);
	const [loadingOptions, setLoadingOptions] = useState(false);

	useEffect(() => {
		setTree(parseSankeyTree(Array.isArray(value) ? value : []));
	}, [value, open]);

	useEffect(() => {
		if (!open || !standardId || standardId <= 0) {
			return undefined;
		}

		let cancelled = false;
		setLoadingOptions(true);

		const parsedForLookup = parseSankeyTree(Array.isArray(value) ? value : []);

		const apply = (m, dps) => {
			if (!cancelled) {
				setMetrics(m ?? []);
				setDataPoints(dps ?? []);
			}
		};

		const topicIds = topicId > 0 ? [topicId] : [];

		StandardService.getAllMetricsAndDatapoints(standardId, null, topicIds)
			.then((res) => {
				if (cancelled || res.status !== 200) return;
				let nextMetrics = res.data?.metrics ?? [];
				let nextDps = res.data?.dataPoints ?? [];

				if (
					topicId > 0 &&
					treeHasUnresolvableRefs(parsedForLookup, nextMetrics, nextDps)
				) {
					return StandardService.getAllMetricsAndDatapoints(standardId, null, []).then((res2) => {
						if (cancelled || res2.status !== 200) return;
						apply(res2.data?.metrics ?? [], res2.data?.dataPoints ?? []);
					});
				}
				apply(nextMetrics, nextDps);
			})
			.catch(() => {
				if (!cancelled) {
					setMetrics([]);
					setDataPoints([]);
				}
			})
			.finally(() => {
				if (!cancelled) setLoadingOptions(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, standardId, topicId, value]);

	const addRootNode = () => setTree((prev) => [...prev, emptyNode()]);
	const updateRoot = (idx, updated) => {
		const next = [...tree];
		next[idx] = updated;
		setTree(next);
	};
	const deleteRoot = (idx) => setTree((prev) => prev.filter((_, i) => i !== idx));

	const handleSave = () => {
		const isValid = (n) => !!n.refId && (!n.children || n.children.every(isValid));
		if (tree.length > 0 && !tree.every(isValid)) {
			return;
		}
		onSave(persistedSankeyTree(tree));
	};

	if (!open) return null;

	return (
		<Modal show={open} onHide={onClose} size="xl" centered scrollable dialogClassName="sankey-config-modal">
			<Modal.Header closeButton>
				<Modal.Title>{t("SankeyDiagramConfigModal.title.label")}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{loadingOptions && (
					<div className="d-flex align-items-center gap-2 text-muted mb-0">
						<Spinner animation="border" size="sm" role="status" aria-hidden />
						<span>{t("Loading.label")}</span>
					</div>
				)}
				{tree.length > 0 && (
					<div className="mb-0">
						<Button variant="GroupStake" className="btn-outline-primary" onClick={addRootNode} disabled={loadingOptions} type="button">
							<BiPlus className="me-1" aria-hidden />
							{t("SankeyDiagramConfigModal.add_root.label")}
						</Button>
					</div>
				)}
				{tree.length === 0 && (
					<div className="sankey-config-empty">
						<div className="sankey-config-empty__icon" aria-hidden>
							<BiNetworkChart />
						</div>
						<p className="sankey-config-empty__text">{t("SankeyDiagramConfigModal.no_nodes.label")}</p>
						<Button variant="primary" size="sm" onClick={addRootNode} disabled={loadingOptions} type="button">
							<BiPlus className="me-1" aria-hidden />
							{t("SankeyDiagramConfigModal.add_root.label")}
						</Button>
					</div>
				)}
				{tree.length > 0 && (
					<div className="sankey-config-tree-wrapper">
						<ul className="sankey-config-tree">
							{tree.map((node, idx) => (
								<SankeyNodeEditor
									key={`root-${idx}`}
									t={t}
									branchKey={`r${idx}`}
									node={node}
									metrics={metrics}
									dataPoints={dataPoints}
									onChange={(updated) => updateRoot(idx, updated)}
									onDelete={() => deleteRoot(idx)}
								/>
							))}
						</ul>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onClose} type="button">
					{t("Cancel.label")}
				</Button>
				<Button variant="primary" onClick={handleSave} type="button">
					{t("Save.label")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default withTranslation()(SankeyDiagramConfigModalBase);
